// src/review/ReviewNotesPage.jsx
import { useEffect, useState, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { API_BASE } from '../api';
import {
  Box, Typography, Paper, Breadcrumbs, Link,
  CircularProgress, InputBase, Snackbar, Alert, Tooltip, Button,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

const AUDIENCE_LABEL = {
  visione_interna: 'Visione interna', capo_progetto: 'Capo progetto',
  autori: 'Autori', produttore: 'Produttore', rete: 'Rete',
  curatore_editoriale: 'Curatore editoriale',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function ReviewNotesPage() {
  const { prodId, revisionId } = useParams();
  const navigate = useNavigate();

  const [prod, setProd] = useState(null);
  const [revision, setRevision] = useState(null);
  const [notes, setNotes] = useState([]);
  const [generalNotes, setGeneralNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, revRes, notesRes] = await Promise.all([
        fetch(`${API_BASE}/productions/${prodId}`),
        fetch(`${API_BASE}/revisions/${revisionId}`),
        fetch(`${API_BASE}/revisions/${revisionId}/notes`),
      ]);
      const prodData = await prodRes.json().catch(() => ({}));
      const revData = await revRes.json().catch(() => ({}));
      const notesData = await notesRes.json().catch(() => []);
      setProd(prodData);
      setRevision(revData);
      const allNotes = Array.isArray(notesData) ? notesData : [];
      setNotes(allNotes.filter(n => !(String(n.author || '').trim().toLowerCase() === 'generale' && (n.timecode === '00:00:00:00' || !n.timecode))));
      setGeneralNotes(allNotes.filter(n => String(n.author || '').trim().toLowerCase() === 'generale' && (n.timecode === '00:00:00:00' || !n.timecode)));
    } catch (e) {
      console.error('Errore caricamento:', e);
    } finally {
      setLoading(false);
    }
  }, [prodId, revisionId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateAction = async (noteId, value) => {
    setNotes(ns => ns.map(n => n.id === noteId ? { ...n, action_taken: value } : n));
    try {
      await fetch(`${API_BASE}/revisions/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_taken: value }),
      });
    } catch (e) {
      showSnack('Errore salvataggio', 'error');
    }
  };

  // ── EXPORT Avid markers ──
  const handleExport = () => {
    if (!notes.length) return;
    const dest = (AUDIENCE_LABEL[revision?.audience] || revision?.audience || 'JKL').toUpperCase().replace(/\s+/g, '_');
    const lines = notes.map(n => {
      const tc = (n.timecode || n.tc_smpte || '00:00:00:00').trim();
      const text = (n.note_text || n.text || '').replace(/\t|\n|\r/g, ' ').trim();
      return `${dest}\t${tc}\tV1\tred\t${text}\t1`;
    });
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `markers_ep${revision?.episode}_${dest}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showSnack('Marker Avid esportati');
  };

  // ── IMPORT Avid markers → action_taken per ordine ──
  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const lines = ev.target.result
          .split('\n')
          .map(l => l.trim())
          .filter(l => l.length > 0);

        const updates = [];
        lines.forEach((line, idx) => {
          const parts = line.split('\t');
          if (parts.length < 5) return;
          const actionText = parts[4]?.trim() || '';
          const note = notes[idx];
          if (note && actionText) {
            updates.push({ id: note.id, action_taken: actionText });
          }
        });

        if (!updates.length) {
          showSnack('Nessun marker trovato nel file', 'warning');
          setImporting(false);
          return;
        }

        // Salva tutti in parallelo
        await Promise.all(updates.map(u =>
          fetch(`${API_BASE}/revisions/notes/${u.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action_taken: u.action_taken }),
          })
        ));

        // Aggiorna stato locale
        setNotes(ns => ns.map(n => {
          const upd = updates.find(u => u.id === n.id);
          return upd ? { ...n, action_taken: upd.action_taken } : n;
        }));

        showSnack(`${updates.length} marker importati`);
      } catch (err) {
        showSnack('Errore importazione', 'error');
        console.error(err);
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file);
  };

  // ── EXPORT PDF report ──
  const handleExportPdf = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const dest = (AUDIENCE_LABEL[revision?.audience] || revision?.audience || '').toUpperCase();
    const prodName = prod?.name || '';
    const pageW = 210;
    const margin = 16;
    const colW = (pageW - margin * 2) / 2;
    let y = 20;

    // Header — sfondo scuro solo per il brand
    doc.setFillColor(10, 10, 15);
    doc.rect(0, 0, pageW, 28, 'F');
    doc.setTextColor(212, 165, 80);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('JKL', margin, 14);
    doc.setTextColor(240, 236, 228);
    doc.setFontSize(11);
    doc.text(`${prodName} — Ep. ${revision?.episode} · ${dest}`, margin + 12, 14);
    doc.setTextColor(160, 155, 145);
    doc.setFontSize(8);
    doc.text(`Report note visione · ${new Date().toLocaleDateString('it-IT')}`, margin, 23);
    y = 38;

    // Sezione note generali
    if (generalNotes.length > 0) {
      doc.setFillColor(240, 235, 220);
      doc.rect(margin, y - 4, pageW - margin * 2, 8, 'F');
      doc.setTextColor(100, 80, 20);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('CONSIDERAZIONI GENERALI', margin + 2, y);
      y += 8;
      generalNotes.forEach(n => {
        const txt = (n.note_text || n.text || '').trim();
        if (!txt) return;
        const lines = doc.splitTextToSize(txt, pageW - margin * 2 - 4);
        const h = lines.length * 4.5 + 6;
        if (y + h > 280) { doc.addPage(); y = 20; }
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(20, 20, 20);
        doc.setFontSize(8);
        doc.text(lines, margin + 2, y);
        y += h;
      });
      y += 6;
    }
    // Intestazioni colonne — sfondo grigio chiaro
    doc.setFillColor(230, 230, 230);
    doc.rect(margin, y - 5, colW - 2, 8, 'F');
    doc.rect(margin + colW + 2, y - 5, colW - 2, 8, 'F');
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.text('NOTA', margin + 2, y);
    doc.text('COSA E STATO FATTO', margin + colW + 4, y);
    y += 6;

    doc.setFont('helvetica', 'normal');
    notes.forEach((n, idx) => {
      const tc = n.timecode || n.tc_smpte || '—';
      const noteText = (n.note_text || n.text || '').trim();
      const actionText = (n.action_taken || '').trim();

      const noteLines = doc.splitTextToSize(noteText, colW - 10);
      const actionLines = doc.splitTextToSize(actionText || '—', colW - 10);
      const rowH = Math.max(noteLines.length, actionLines.length) * 4.5 + 10;

      if (y + rowH > 280) {
        doc.addPage();
        y = 20;
      }

      // Sfondo riga alternato — bianco / grigio chiarissimo
      if (idx % 2 === 0) {
        doc.setFillColor(247, 247, 247);
        doc.rect(margin, y - 4, pageW - margin * 2, rowH, 'F');
      } else {
        doc.setFillColor(255, 255, 255);
        doc.rect(margin, y - 4, pageW - margin * 2, rowH, 'F');
      }

      // Numero nota + timecode — grigio scuro
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text(`#${idx + 1}`, margin + 2, y);
      doc.setFont('courier', 'normal');
      doc.setTextColor(60, 60, 60);
      doc.text(tc, margin + 8, y);

      // Testo nota — nero
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(20, 20, 20);
      doc.setFontSize(8);
      doc.text(noteLines, margin + 2, y + 5);

      // Linea divisoria verticale
      doc.setDrawColor(200, 200, 200);
      doc.line(margin + colW, y - 4, margin + colW, y + rowH - 2);

      // Cosa è stato fatto
      if (actionText) {
        doc.setTextColor(20, 20, 20);
      } else {
        doc.setTextColor(160, 160, 160);
      }
      doc.text(actionLines, margin + colW + 4, y + 5);

      // Separatore orizzontale
      doc.setDrawColor(210, 210, 210);
      doc.line(margin, y + rowH - 2, pageW - margin, y + rowH - 2);

      y += rowH;
    });

    // Footer
    doc.setTextColor(160, 160, 160);
    doc.setFontSize(7);
    doc.text('Generato da JKL · joinjkl.com', margin, 292);

    doc.save(`report_ep${revision?.episode}_${dest}.pdf`);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <CircularProgress sx={{ color: '#d4a550' }} />
    </Box>
  );

  const dest = AUDIENCE_LABEL[revision?.audience] || revision?.audience || '—';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3, '& .MuiBreadcrumbs-separator': { opacity: 0.4 } }}>
        <Link component={RouterLink} underline="hover" sx={{ fontSize: 13, color: 'text.secondary' }} to="/">Dashboard</Link>
        <Link component={RouterLink} underline="hover" sx={{ fontSize: 13, color: 'text.secondary' }} to="/productions">Produzioni</Link>
        <Link component={RouterLink} underline="hover" sx={{ fontSize: 13, color: 'text.secondary' }} to={`/productions/${prodId}`}>{prod?.name || '—'}</Link>
        <Typography sx={{ fontSize: 13, color: 'text.primary', fontWeight: 600 }}>
          Note Ep. {revision?.episode} · {dest}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{
        p: 3, mb: 3, borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.07)',
        borderLeft: '3px solid rgba(212,165,80,0.5)',
        background: 'rgba(255,255,255,0.02)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
              <Box sx={{
                px: 1.25, py: 0.3, borderRadius: '6px',
                background: 'rgba(212,165,80,0.1)', border: '1px solid rgba(212,165,80,0.25)',
                color: '#d4a550', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
              }}>
                EP. {revision?.episode}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.3 }}>{dest}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>· {prod?.name}</Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
              Inviato il {formatDate(revision?.created_at)} · {notes.length} {notes.length === 1 ? 'nota' : 'note'}
            </Typography>
          </Box>

          {/* Azioni */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Esporta marker per Avid Media Composer" arrow>
              <span>
                <Button
                  variant="outlined" size="small"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={handleExport}
                  disabled={!notes.length}
                  sx={{
                    borderColor: 'rgba(212,165,80,0.35)', color: '#d4a550', fontSize: 12,
                    '&:hover': { borderColor: '#d4a550', background: 'rgba(212,165,80,0.06)' },
                  }}
                >
                  Export Avid
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Esporta report PDF" arrow>
              <span>
                <Button
                  variant="outlined" size="small"
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  onClick={handleExportPdf}
                  disabled={!notes.length}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.15)', color: 'text.secondary', fontSize: 12,
                    '&:hover': { borderColor: 'rgba(212,165,80,0.35)', color: '#d4a550' },
                  }}
                >
                  Export PDF
                </Button>
              </span>
            </Tooltip>

            <Tooltip title="Importa marker da Avid — compila 'Cosa è stato fatto' per ordine" arrow>
              <span>
                <Button
                  variant="outlined" size="small" component="label"
                  startIcon={<FileUploadOutlinedIcon />}
                  disabled={importing || !notes.length}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.15)', color: 'text.secondary', fontSize: 12,
                    '&:hover': { borderColor: 'rgba(212,165,80,0.35)', color: '#d4a550' },
                  }}
                >
                  {importing ? 'Importazione…' : 'Import Avid'}
                  <input hidden type="file" accept=".txt" onChange={handleImport} />
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Lista note */}
      {notes.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '12px' }}>
          <Typography sx={{ fontSize: 14, color: 'text.disabled' }}>Nessuna nota per questa revisione.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {generalNotes.length > 0 && (
            <Box sx={{ mb: 2, p: 2, background: 'rgba(212,165,80,0.06)', border: '1px solid rgba(212,165,80,0.2)', borderRadius: 2 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#d4a550', mb: 1, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Considerazioni generali</Typography>
              {generalNotes.map((n, i) => (
                <Typography key={i} sx={{ fontSize: 13, color: 'text.primary', whiteSpace: 'pre-wrap' }}>{n.note_text || n.text}</Typography>
              ))}
            </Box>
          )}
          {notes.map((n, idx) => (
            <Paper key={n.id} sx={{
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.07)',
              background: 'rgba(255,255,255,0.02)',
              overflow: 'hidden',
              transition: 'border-color .15s',
              '&:hover': { borderColor: 'rgba(212,165,80,0.15)' },
            }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 80 }}>

                {/* Sinistra: nota originale */}
                <Box sx={{
                  p: 2, borderRight: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', flexDirection: 'column', gap: 1,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 1, py: 0.25, borderRadius: '5px',
                      background: 'rgba(212,165,80,0.08)', border: '1px solid rgba(212,165,80,0.15)',
                      color: '#d4a550', fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                    }}>
                      <PlayArrowRoundedIcon sx={{ fontSize: 12 }} />
                      {n.timecode || n.tc_smpte || '—'}
                    </Box>
                    <Typography sx={{ fontSize: 10, color: 'text.disabled', letterSpacing: '0.06em' }}>#{idx + 1}</Typography>
                    {n.author && (
                      <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>· {n.author}</Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: '#f0ece4', lineHeight: 1.55 }}>
                    {n.note_text || n.text}
                  </Typography>
                </Box>

                {/* Destra: cosa è stato fatto */}
                <Box sx={{
                  p: 2, display: 'flex', flexDirection: 'column', gap: 0.5,
                  background: n.action_taken ? 'rgba(212,165,80,0.03)' : 'transparent',
                  transition: 'background .2s',
                }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                    textTransform: 'uppercase', mb: 0.5,
                    color: n.action_taken ? 'rgba(212,165,80,0.6)' : 'text.disabled',
                  }}>
                    Cosa è stato fatto
                  </Typography>
                  <InputBase
                    multiline fullWidth
                    placeholder="Descrivi le modifiche apportate…"
                    value={n.action_taken || ''}
                    onChange={(e) => setNotes(ns => ns.map(x => x.id === n.id ? { ...x, action_taken: e.target.value } : x))}
                    onBlur={(e) => updateAction(n.id, e.target.value)}
                    sx={{ fontSize: 13, lineHeight: 1.55, color: '#f0ece4', '& textarea': { minHeight: 48 } }}
                  />
                </Box>

              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
